import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Spinner,
  TextInput,
  toast,
} from 'component-library';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components';
import { SuspenseBoundary } from '@/components/SuspenseBoundary';
import { useJobSubscriptions } from '@/hooks/useJobSubscriptions';
import { usePostNdiManualJobSubscriptionCost } from '@/hooks/useNdiCredits';

type ManualCostFormValues = {
  job_subscription: string;
  amount: string;
  description: string;
};

type ManualCostDialogProps = {
  open: boolean;
  onClose: () => void;
  organizationId: string;
};

export const ManualCostDialog = ({ open, onClose, organizationId }: ManualCostDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className='min-w-[30vw]'>
        <DialogHeader>
          <DialogTitle>{t('shared.ndiOrganizationSubscriptions.financialPage.manualCostDialog.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {open && (
            <SuspenseBoundary fallback={<Spinner size={10} />}>
              <ManualCostFormContent organizationId={organizationId} onClose={onClose} />
            </SuspenseBoundary>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

const ManualCostFormContent = ({ organizationId, onClose }: { organizationId: string; onClose: () => void }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const postMutation = usePostNdiManualJobSubscriptionCost();
  const { data: jobSubscriptionsData } = useJobSubscriptions({ org: organizationId });
  const jobSubscriptions = jobSubscriptionsData.results;

  const form = useForm<ManualCostFormValues>({
    defaultValues: { job_subscription: '', amount: '', description: '' },
  });

  const handleClose = () => {
    form.reset({ job_subscription: '', amount: '', description: '' });
    onClose();
  };

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await postMutation.mutateAsync({
        bodyData: {
          job_subscription: values.job_subscription,
          amount: values.amount,
          description: values.description,
        },
      });

      queryClient.invalidateQueries({ queryKey: ['ndi-credits-balance', organizationId] });
      queryClient.invalidateQueries({ queryKey: ['ndi-credits-transactions', organizationId] });
      toast({ type: 'success', title: t('shared.ndiOrganizationSubscriptions.financialPage.toast.manualCostSuccess') });
      handleClose();
    } catch {
      toast({ type: 'error', title: t('shared.ndiOrganizationSubscriptions.financialPage.toast.manualCostError') });
    }
  });

  const requiredLabel = t('global.ui.form.input.required');

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
        <FormField
          control={form.control}
          name='job_subscription'
          rules={{ required: requiredLabel }}
          render={({ field: { value, ...field } }) => (
            <FormItem>
              <FormLabel>
                {t('shared.ndiOrganizationSubscriptions.financialPage.manualCostDialog.jobSubscription')} *
              </FormLabel>
              <Select value={value} {...field}>
                <FormControl>
                  <SelectTrigger
                    placeholder={t(
                      'shared.ndiOrganizationSubscriptions.financialPage.manualCostDialog.jobSubscriptionPlaceholder',
                    )}
                  />
                </FormControl>
                <SelectContent>
                  {jobSubscriptions.map((jobSubscription) => (
                    <SelectItem key={jobSubscription.id} value={jobSubscription.id}>
                      {jobSubscription.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage>{form.formState.errors.job_subscription?.message}</FormMessage>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='amount'
          rules={{
            required: requiredLabel,
            validate: (v) =>
              Number.isFinite(Number(v)) || t('shared.ndiOrganizationSubscriptions.formRules.invalidNumber'),
          }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('shared.ndiOrganizationSubscriptions.financialPage.manualCostDialog.amount')} *</FormLabel>
              <FormControl>
                <TextInput {...field} inputMode='numeric' />
              </FormControl>
              <span className='typography-caption text-text-secondary'>
                {t('shared.ndiOrganizationSubscriptions.financialPage.manualCostDialog.amountHelp')}
              </span>
              <FormMessage>{form.formState.errors.amount?.message}</FormMessage>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='description'
          rules={{ required: requiredLabel }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('shared.ndiOrganizationSubscriptions.financialPage.manualCostDialog.description')} *
              </FormLabel>
              <FormControl>
                <textarea
                  {...field}
                  className='flex min-h-[100px] w-full rounded-xl border border-neutral-black-12 px-4 py-4 placeholder:text-text-disabled resize-none focus:border-text-primary focus:outline-none'
                />
              </FormControl>
              <FormMessage>{form.formState.errors.description?.message}</FormMessage>
            </FormItem>
          )}
        />
        <Button
          type='submit'
          className='self-end w-fit px-6'
          loading={postMutation.isPending}
          disabled={postMutation.isPending}
        >
          {t('global.ui.buttons.save')}
        </Button>
      </form>
    </Form>
  );
};
